/**
 * Cycle lifecycle display labels — derived from authoritative DB signals.
 *
 * degap_cycles.status uses enum cycle_status: draft | active | paused | completed | archived.
 * "Ratified" / "Published" / "Closed" are display tiers composed with priority_sets and cycle_reports.
 */

import type { CycleStatus } from '@/types/prioritization/degapCycle';

export type CycleLifecycleDisplayStatus =
  | 'draft'
  | 'ready'
  | 'active'
  | 'paused'
  | 'ratified'
  | 'published'
  | 'closed';

/** In-flight session states (not yet published / abandoned). */
export const SESSION_STATES_COUNTING_AS_ACTIVE = ['DRAFT', 'CRITERIA_FINALIZED', 'RUN_COMPLETE'] as const;

export type CycleLifecycleSignals = {
  dbStatus: CycleStatus;
  currentPhase: string;
  currentLoopId: string | null | undefined;
  hasAnyCatchballLoop: boolean;
  hasActiveSession: boolean;
  hasSubmittedCatchballProposal: boolean;
  hasPublishedCycleReport: boolean;
  hasRatifiedCycleReport: boolean;
  hasRatifiedOrPublishedPrioritySet: boolean;
  /**
   * W-1 (2026-07-08): design pass finished (metadata.designerLaunchedAt set) but no
   * execution activity yet — the cycle is designed and awaiting its first request.
   * Optional so existing callers/tests without designer context keep working.
   */
  hasCompletedDesign?: boolean;
};

/** Anything that means the cycle's work has begun: a phase past DECIDE, a ball, a session, a proposal. */
function hasExecutionActivity(s: CycleLifecycleSignals): boolean {
  const phase = (s.currentPhase || 'DECIDE').toString();
  return (
    s.dbStatus === 'active' ||
    phase !== 'DECIDE' ||
    (s.currentLoopId != null && String(s.currentLoopId).length > 0) ||
    s.hasAnyCatchballLoop ||
    s.hasActiveSession ||
    s.hasSubmittedCatchballProposal
  );
}

function isRatified(s: CycleLifecycleSignals): boolean {
  return s.hasPublishedCycleReport || s.hasRatifiedCycleReport || s.hasRatifiedOrPublishedPrioritySet;
}

export function deriveCycleLifecycleDisplayStatus(s: CycleLifecycleSignals): CycleLifecycleDisplayStatus {
  if (s.dbStatus === 'completed' || s.dbStatus === 'archived') return 'closed';
  if (s.dbStatus === 'paused') return 'paused';
  if (s.hasPublishedCycleReport) return 'published';
  if (isRatified(s)) return 'ratified';

  if (hasExecutionActivity(s)) return 'active';
  // W-1: distinguish *designed, first request pending* from plain draft — the chip
  // must not say "draft" after the Designer's one-way door has closed.
  if (s.hasCompletedDesign) return 'ready';
  return 'draft';
}

export function cycleLifecycleDisplayLabel(status: CycleLifecycleDisplayStatus): string {
  switch (status) {
    case 'closed':
      return 'Closed';
    case 'paused':
      return 'Paused';
    case 'published':
      return 'Published';
    case 'ratified':
      return 'Ratified';
    case 'active':
      return 'Active';
    case 'ready':
      return 'Ready';
    default:
      return 'Draft';
  }
}

/* ── The path a cycle takes ───────────────────────────────────────────────── */

/**
 * The statuses above in the order a cycle moves through them. Canon 21 §3.3
 * names DRAFT → ACTIVE → EXECUTION → RATIFIED → PUBLISHED → ARCHIVED; the app
 * adds Ready (W-1: designed, first request not sent), shows Active and
 * Execution as one step, and calls Archived (and Completed) Closed.
 * Paused is not a step: it stops the cycle where it is.
 */
export const CYCLE_LIFECYCLE_PATH = ['draft', 'ready', 'active', 'ratified', 'published', 'closed'] as const;
export type CycleLifecyclePathStatus = (typeof CYCLE_LIFECYCLE_PATH)[number];

/** When the cycle reached each step, where the record keeps it. Null: not reached, or not recorded. */
export interface CycleLifecycleDates {
  createdAt: string | null;
  /** metadata.designerLaunchedAt — the Cycle Designer's launch. */
  designedAt: string | null;
  /** The earliest of started_at, the first ball, the first session. */
  startedAt: string | null;
  ratifiedAt: string | null;
  publishedAt: string | null;
  closedAt: string | null;
}

export type CyclePathStepState = 'done' | 'now' | 'not_yet' | 'skipped';

export interface CyclePathStep {
  status: CycleLifecyclePathStatus;
  label: string;
  state: CyclePathStepState;
  at: string | null;
}

/**
 * Each step of the path with whether the cycle has passed it, is on it, has
 * not reached it, or went past it without it (no design pass, so never Ready;
 * closed before a report was ratified). A paused cycle is "now" on the step it
 * was paused at.
 */
export function cycleLifecyclePath(s: CycleLifecycleSignals, dates: CycleLifecycleDates): CyclePathStep[] {
  const where = deriveCycleLifecycleDisplayStatus(s.dbStatus === 'paused' ? { ...s, dbStatus: 'active' } : s);
  const now = where as CycleLifecyclePathStatus;
  const nowIndex = CYCLE_LIFECYCLE_PATH.indexOf(now);
  const passed: Record<CycleLifecyclePathStatus, boolean> = {
    draft: true,
    ready: Boolean(s.hasCompletedDesign),
    active: hasExecutionActivity(s),
    ratified: isRatified(s),
    published: s.hasPublishedCycleReport,
    closed: s.dbStatus === 'completed' || s.dbStatus === 'archived',
  };
  const at: Record<CycleLifecyclePathStatus, string | null> = {
    draft: dates.createdAt,
    ready: dates.designedAt,
    active: dates.startedAt,
    ratified: dates.ratifiedAt,
    published: dates.publishedAt,
    closed: dates.closedAt,
  };
  return CYCLE_LIFECYCLE_PATH.map((status, i) => {
    const state: CyclePathStepState =
      status === now ? 'now' : passed[status] ? 'done' : i < nowIndex ? 'skipped' : 'not_yet';
    return { status, label: cycleLifecycleDisplayLabel(status), state, at: state === 'not_yet' || state === 'skipped' ? null : at[status] };
  });
}

export function cycleLifecycleDisplayBadgeClass(status: CycleLifecycleDisplayStatus): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'draft':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    case 'ready':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'paused':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'ratified':
      return 'bg-sky-100 text-sky-800 border-sky-200';
    case 'published':
      return 'bg-violet-100 text-violet-800 border-violet-200';
    case 'closed':
      return 'bg-neutral-200 text-neutral-600 border-neutral-300';
    default:
      return 'bg-gray-100 text-gray-500 border-gray-200';
  }
}

/**
 * When persisting phase changes outside transition_cycle_phase RPC, align lifecycle status
 * the same way migration 328 does (non-DECIDE ⇒ active unless paused/completed/archived).
 */
export function alignDegapCycleLifecycleStatusWithPhase<T extends { status: CycleStatus; currentPhase: string; startedAt?: Date }>(
  cycle: T,
  now: Date = new Date(),
): T {
  if (cycle.status === 'paused' || cycle.status === 'completed' || cycle.status === 'archived') {
    return cycle;
  }
  if (cycle.currentPhase && cycle.currentPhase !== 'DECIDE') {
    return {
      ...cycle,
      status: 'active',
      startedAt: cycle.startedAt ?? now,
    };
  }
  return cycle;
}

/**
 * Whether the cycle list's Status filter asks for archived cycles. An archived
 * cycle reads as Closed, so Closed is the one option that shows it; with no
 * Status filter set, archived cycles stay out of the list.
 */
export function showsArchivedCycles(statusFilter: string[] | undefined): boolean {
  return statusFilter?.includes('closed') ?? false;
}
